import React, { useState, useEffect } from 'react';
import {
  X, User, Mail, Phone, Building2, Calendar, Shield, Key, Clock,
  Save, AlertTriangle, Check, Eye, EyeOff, MapPin, Briefcase, Users
} from 'lucide-react';

const UserManagementPanel = ({ userId, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);

  // Form states
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: '',
    require_change_on_login: false
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (userId) {
      loadUserDetails();
      loadAllUsers();
    }
  }, [userId]);

  useEffect(() => {
    if (activeTab === 'security' && userId) {
      loadLoginHistory();
    }
  }, [activeTab, userId]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/users/${userId}/details`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setFormData(data);
      } else {
        setError('Failed to load user details');
      }
    } catch (err) {
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.filter(u => u.role !== 'client'));
      }
    } catch (err) {
      console.error('Failed to load users for manager dropdown');
    }
  };

  const loadLoginHistory = async () => {
    try {
      const response = await fetch(`/api/users/${userId}/login-history`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLoginHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to load login history');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const response = await fetch(`/api/users/${userId}/details`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess('User updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        loadUserDetails();
        if (onUpdate) onUpdate();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update user');
      }
    } catch (err) {
      setError('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const response = await fetch(`/api/users/${userId}/set-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_password: passwordData.new_password,
          require_change_on_login: passwordData.require_change_on_login
        })
      });

      if (response.ok) {
        setSuccess('Password set successfully');
        setPasswordData({ new_password: '', confirm_password: '', require_change_on_login: false });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to set password');
      }
    } catch (err) {
      setError('Failed to set password');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA for this user?')) return;

    try {
      const response = await fetch(`/api/users/${userId}/disable-2fa`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        setSuccess('2FA disabled successfully');
        loadUserDetails();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to disable 2FA');
      }
    } catch (err) {
      setError('Failed to disable 2FA');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-virgo-navy-700 rounded-lg p-8">
          <div className="text-virgo-teal-400">Loading user details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-end z-50">
      <div className="w-full max-w-2xl bg-virgo-navy-800 h-full overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-virgo-navy-700 border-b border-virgo-navy-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-virgo-navy-600 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-virgo-teal-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{user?.full_name || user?.username}</h2>
              <p className="text-virgo-teal-400 text-sm">{user?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-virgo-navy-600 px-6">
          <nav className="flex space-x-6">
            {[
              { id: 'profile', label: 'Profile', icon: User },
              { id: 'security', label: 'Security', icon: Shield },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-virgo-teal-400 text-virgo-teal-400'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}
        {success && (
          <div className="mx-6 mt-4 bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-virgo-navy-700 rounded-lg p-5 border border-virgo-navy-600">
                <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Full Name</label>
                    <input
                      type="text"
                      value={formData.full_name || ''}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Display Name</label>
                    <input
                      type="text"
                      value={formData.display_name || ''}
                      onChange={(e) => handleChange('display_name', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Username</label>
                    <input
                      type="text"
                      value={formData.username || ''}
                      onChange={(e) => handleChange('username', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Role</label>
                    <select
                      value={formData.role || ''}
                      onChange={(e) => handleChange('role', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="client">Client</option>
                      <option value="staff">Staff</option>
                      <option value="attorney">Attorney</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="col-span-2 flex items-center gap-3">
                    <label className="text-sm font-medium text-virgo-teal-400">Active</label>
                    <button
                      onClick={() => handleChange('is_active', !formData.is_active)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.is_active ? 'bg-green-500' : 'bg-gray-600'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.is_active ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <span className={`text-sm ${formData.is_active ? 'text-green-400' : 'text-gray-400'}`}>
                      {formData.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-virgo-navy-700 rounded-lg p-5 border border-virgo-navy-600">
                <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Primary Email</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Secondary Email</label>
                    <input
                      type="email"
                      value={formData.secondary_email || ''}
                      onChange={(e) => handleChange('secondary_email', e.target.value)}
                      placeholder="Recovery/alternate email"
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white placeholder-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Work Phone</label>
                    <input
                      type="tel"
                      value={formData.work_phone || ''}
                      onChange={(e) => handleChange('work_phone', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Mobile Phone</label>
                    <input
                      type="tel"
                      value={formData.mobile_phone || ''}
                      onChange={(e) => handleChange('mobile_phone', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Employee Info */}
              <div className="bg-virgo-navy-700 rounded-lg p-5 border border-virgo-navy-600">
                <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Employee Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Job Title</label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Department</label>
                    <input
                      type="text"
                      value={formData.department || ''}
                      onChange={(e) => handleChange('department', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Employee ID</label>
                    <input
                      type="text"
                      value={formData.employee_id || ''}
                      onChange={(e) => handleChange('employee_id', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Manager</label>
                    <select
                      value={formData.manager_id || ''}
                      onChange={(e) => handleChange('manager_id', e.target.value ? parseInt(e.target.value) : 0)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">No Manager</option>
                      {allUsers.filter(u => u.id !== userId).map(u => (
                        <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.role})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Office Location</label>
                    <input
                      type="text"
                      value={formData.office_location || ''}
                      onChange={(e) => handleChange('office_location', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Hire Date</label>
                    <input
                      type="date"
                      value={formData.hire_date ? formData.hire_date.split('T')[0] : ''}
                      onChange={(e) => handleChange('hire_date', e.target.value)}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Set Password */}
              <div className="bg-virgo-navy-700 rounded-lg p-5 border border-virgo-navy-600">
                <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Set Password
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                        className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white pr-10"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Confirm Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white"
                      placeholder="Confirm password"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={passwordData.require_change_on_login}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, require_change_on_login: e.target.checked }))}
                      className="rounded border-virgo-navy-500"
                    />
                    <label className="text-sm text-virgo-teal-400">
                      Require password change on next login
                    </label>
                  </div>
                  <button
                    onClick={handleSetPassword}
                    disabled={saving || !passwordData.new_password}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Key className="w-4 h-4" />
                    Set Password
                  </button>
                </div>
              </div>

              {/* 2FA Status */}
              <div className="bg-virgo-navy-700 rounded-lg p-5 border border-virgo-navy-600">
                <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Two-Factor Authentication
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">2FA Status</p>
                    <p className={`text-sm ${user?.is_2fa_enabled ? 'text-green-400' : 'text-gray-400'}`}>
                      {user?.is_2fa_enabled ? 'Enabled' : 'Not enabled'}
                    </p>
                  </div>
                  {user?.is_2fa_enabled && (
                    <button
                      onClick={handleDisable2FA}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                    >
                      <Shield className="w-4 h-4" />
                      Disable 2FA
                    </button>
                  )}
                </div>
              </div>

              {/* Security Info */}
              <div className="bg-virgo-navy-700 rounded-lg p-5 border border-virgo-navy-600">
                <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Security Details
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-virgo-teal-400">Last Login</span>
                    <span className="text-white">{formatDate(user?.last_login)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-virgo-teal-400">Last Login IP</span>
                    <span className="text-white">{user?.last_login_ip || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-virgo-teal-400">Password Changed</span>
                    <span className="text-white">{formatDate(user?.password_changed_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-virgo-teal-400">Account Created</span>
                    <span className="text-white">{formatDate(user?.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Login History */}
              <div className="bg-virgo-navy-700 rounded-lg p-5 border border-virgo-navy-600">
                <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Recent Login Activity
                </h3>
                {loginHistory.length === 0 ? (
                  <p className="text-gray-400 text-sm">No login history available</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {loginHistory.map((event, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-virgo-navy-600 last:border-0">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            event.status === 'SUCCESS' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {event.action.replace('_', ' ')}
                          </span>
                          <span className="text-gray-400 text-sm">{event.ip_address}</span>
                        </div>
                        <span className="text-gray-500 text-sm">{formatDate(event.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementPanel;
